import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Mic, MicOff, Square } from 'lucide-react';

interface VoiceRecorderProps {
  onTranscription: (text: string) => void;
  onStopSpeaking: () => void;
  onMicStart?: () => void;
  onMicStop?: () => void;
  isAISpeaking: boolean;
  isProcessing: boolean;
  isListening?: boolean;
}

const RecorderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  width: 100%;
`;

const MicButton = styled.button<{ $isRecording: boolean; $isAISpeaking: boolean }>`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: none;
  background: ${props => 
    props.$isAISpeaking ? '#ff4444' : 
    props.$isRecording ? '#ff6b6b' : '#FFCC00'};
  color: white;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const StatusText = styled.div`
  background: rgba(255, 255, 255, 0.9);
  padding: 12px 16px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  font-size: 14px;
  color: #333;
  text-align: center;
  max-width: 160px;
  line-height: 1.3;
`;

const InstructionText = styled.div`
  color: white;
  font-size: 16px;
  font-weight: 600;
  font-family: 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
  text-align: center;
  max-width: 160px;
  line-height: 1.4;
  margin-top: 10px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  letter-spacing: 0.5px;
`;

declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}

const VoiceRecorder: React.FC<VoiceRecorderProps> = ({
  onTranscription,
  onStopSpeaking,
  onMicStart,
  onMicStop,
  isAISpeaking,
  isProcessing,
  isListening = false
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const recognitionRef = useRef<any>(null);

  const startListening = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'es-ES';
      
      recognitionRef.current.onstart = () => {
        console.log('🎤 Reconocimiento de voz iniciado');
        setIsRecording(true);
        onMicStart?.();
        setTranscript('');
      };
      
      recognitionRef.current.onresult = (event: any) => {
        let interimTranscript = '';
        let finalTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }
        
        setTranscript(finalTranscript || interimTranscript);
        
        if (finalTranscript) {
          console.log('📝 Transcripción final:', finalTranscript);
          onTranscription(finalTranscript);
          setTranscript('');
        }
      };
      
      recognitionRef.current.onerror = (event: any) => {
        console.error('❌ Error en reconocimiento de voz:', event.error);
        setIsRecording(false);
        onMicStop?.();
      };
      
      recognitionRef.current.onend = () => {
        console.log('🔚 Reconocimiento de voz terminado');
        setIsRecording(false);
        onMicStop?.();
        setTranscript('');
      };
      
      recognitionRef.current.start();
    } else {
      alert('Tu navegador no soporta reconocimiento de voz');
    }
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
  };

  const handleMicClick = () => {
    if (isAISpeaking) {
      onStopSpeaking();
      return;
    }

    if (isRecording) {
      stopListening();
    } else if (!isProcessing) {
      startListening();
    }
  };

  const getButtonIcon = () => {
    if (isAISpeaking) return <Square size={32} />;
    if (isRecording || isListening) return <MicOff size={32} />;
    return <Mic size={32} />;
  };

  const getStatusText = () => {
    if (isProcessing) return 'Procesando...';
    if (isAISpeaking) return 'Greta hablando';
    if (isRecording || isListening) return 'Escuchando...';
    return 'Listo para escuchar';
  };

  const getInstructionText = () => {
    if (isAISpeaking) return 'Presiona para detener';
    if (isRecording || isListening) return 'Habla ahora';
    return 'Toca para hablar con Greta';
  };

  return (
    <RecorderContainer>
      <StatusText>{getStatusText()}</StatusText>
      
      <MicButton
        onClick={handleMicClick}
        $isRecording={isRecording || isListening}
        $isAISpeaking={isAISpeaking}
      >
        {getButtonIcon()}
      </MicButton>
      
      <InstructionText>{getInstructionText()}</InstructionText>
    </RecorderContainer>
  );
};

export default VoiceRecorder;
